/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Checkbox,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { func, object, string, bool } from "prop-types";
import cogoToast from "cogo-toast";

import CustomInput from "../../../components/CustomInput";
import SearchLocationInput from "../../../components/LocationComponent";
import CustomSelect from "../../../components/CustomSelect";
import AIJobDescriptionIcons from "../../../components/AI/AIJobDescriptionIcons";
import { getCountryAction } from "../../../redux/actions/countryActions";
import { aiJobDescriptionService } from "../../../services/aiJobDescriptionService";
import { CURRENCY_OPTIONS } from "../../../utils/constants";
import icons from "../../../assets/icons/icon-collection.svg";
import {
  createMissingSkillsIfAny,
  getAllSkillsAction,
  createCategorySkillAction,
} from "../../../redux/actions/skillsAction";

const JobDetails = ({
  handleChange,
  updateSelectedCountryID,
  industryOptions,
  levelOptions,
  skillOptions,
  setNewJob,
  newJob,
  duplicateData,
  isOnDuplicatePost,
}) => {
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [externalPlatforms, setExternalPlatforms] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const dispatch = useDispatch();
  const [state, setState] = useState({
    country: selectedCountry || 0,
  });

  const { country, auth } = useSelector((store) => store);
  const { countries } = country;
  const { userData } = auth;

  useEffect(() => {
    getCountryAction(dispatch);
  }, [dispatch]);

  const addSkill = ({ target: { value } }) => {
    const skills = value;
    setSelectedSkills(skills);
    handleChange({ target: { value: skills, name: "skills_required" } });
  };

  const handleSkillsChange = (_event, value) => {
    const normalized = value.map((v) =>
      typeof v === "string" ? v : v?.inputValue ? v.inputValue : v?.title || v
    );
    setSelectedSkills(normalized);
    handleChange({ target: { value: normalized, name: "skills_required" } });
    // Persist new skills to category-create endpoint with title & description
    const existingTitles = new Set(
      (skillOptions || []).map(({ title }) => (title || "").toLowerCase())
    );
    const newTitles = normalized
      .map((t) => (t || "").trim())
      .filter((t) => t && !existingTitles.has(t.toLowerCase()));
    if (newTitles.length > 0) {
      Promise.all(
        newTitles.map((title) =>
          createCategorySkillAction({ title, description: title }, dispatch)
        )
      ).then(() => getAllSkillsAction(dispatch));
    }
  };

  const handleExternalPlatformChange = (platform) => {
    let updatedPlatforms;
    if (externalPlatforms.includes(platform)) {
      updatedPlatforms = externalPlatforms.filter((p) => p !== platform);
    } else {
      updatedPlatforms = [...externalPlatforms, platform];
    }
    setExternalPlatforms(updatedPlatforms);
    handleChange({
      target: { value: updatedPlatforms, name: "external_platforms" },
    });
  };

  const displaySkills = () => {
    return selectedSkills.map((skill, index) => (
      <span
        className="d-flex align-items-center justify-content-between mt-2 mr-2 py-1 pl-3 outlined"
        key={index.toString()}
      >
        {skill}
        <IconButton onClick={() => removeSkill(skill)}>
          <svg width="10" height="8">
            <use href={`${icons}#close-blue`}></use>
          </svg>
        </IconButton>
      </span>
    ));
  };

  const handleChangeCountry = ({ target: { value } }) => {
    const countryID = value;
    setSelectedCountry(countryID);
    setState({ ...newJob, country: countryID });
    updateSelectedCountryID(countryID);
  };

  const removeSkill = (skillToRemove) => {
    const skillArray = [...selectedSkills];
    const newSkillArray = skillArray.filter((skill) => skill !== skillToRemove);
    setSelectedSkills(newSkillArray);
    handleChange({ target: { value: newSkillArray, name: "skills_required" } });
  };

  const stripHtmlTags = (html) => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").trim();
  };

  const handleWriteWithAI = async () => {
    setAiLoading(true);
    try {
      const currentData = isOnDuplicatePost ? duplicateData : newJob;
      const jobData = {
        company_name: currentData.campany_name || "",
        location: currentData.location || "",
        role_title: currentData.role_title || "",
        reports_to: currentData.reports_to || "",
        job_level: currentData.job_level || "",
        job_type: currentData.job_type || "",
        industry: currentData.industry || "",
        country: state.country || selectedCountry || "",
      };

      if (!jobData.company_name.trim()) {
        cogoToast.warn("Please fill in the company name first");
        return;
      }
      if (!jobData.role_title.trim()) {
        cogoToast.warn("Please fill in the role title first");
        return;
      }

      const generatedDescription =
        await aiJobDescriptionService.generateJobDescription(jobData);
      setNewJob({ ...newJob, about_company: generatedDescription });
      cogoToast.success("Job description generated successfully!");
    } catch (error) {
      const errorMessage =
        error.message ||
        "Failed to generate job description. Please try again.";
      cogoToast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRewriteWithAI = async () => {
    const currentData = isOnDuplicatePost ? duplicateData : newJob;
    const currentContent = currentData.about_company || "";
    const cleanedContent = stripHtmlTags(currentContent);

    if (!cleanedContent) {
      cogoToast.warn("Please add some content to rewrite first");
      return;
    }
    if (cleanedContent.length < 20) {
      cogoToast.warn("Please add more content before using rewrite feature");
      return;
    }

    setAiLoading(true);
    try {
      const jobData = {
        role_title: currentData.role_title || "",
        company_name: currentData.campany_name || "",
      };
      const rewrittenDescription =
        await aiJobDescriptionService.rewriteJobDescription(
          currentContent,
          jobData
        );
      setNewJob({ ...newJob, about_company: rewrittenDescription });
      cogoToast.success("Job description rewritten successfully!");
    } catch (error) {
      const errorMessage =
        error.message || "Failed to rewrite job description. Please try again.";
      cogoToast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  };

  const currentTier = isOnDuplicatePost
    ? duplicateData.job_tier
    : newJob.job_tier;

  return (
    <>
      <div className="row mx-0">
        <div className="col-12 col-md-6">
          <CustomInput
            type="text"
            onChange={handleChange}
            name="campany_name"
            value={
              isOnDuplicatePost
                ? duplicateData.campany_name
                : newJob.campany_name
            }
            placeholder=""
            label="Name Of Company"
            id="name"
          />
        </div>

        <div className="col-12 col-md-6">
          <FormControl
            variant="outlined"
            fullWidth
            className="w-100 mb-4 rounded"
          >
            <SearchLocationInput
              inputStyle="TextField"
              handleLocationChange={(e) =>
                handleChange({ target: { value: e, name: "location" } })
              }
            />
          </FormControl>
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "-5px" }}>
          <CustomSelect
            handleChange={handleChange}
            className="fullwidth-select"
            options={{
              0: "Select Staff Strength",
              "Sole Employee": "Sole Employee",
              "2-20": "2-20",
              "21-50": "21-50",
              "51-200": "51-200",
              "201-500": "201-500",
              "501-1000": "501-1000",
              "1,001-10,000": "1,001-10,000",
              "10,001-50,000": "10,001-50,000",
              "50,001+": "50,001+",
            }}
            id="no_of_staff"
            name="no_of_staff"
            label="Staff Strength"
            currentValue={
              isOnDuplicatePost ? duplicateData.no_of_staff : newJob.no_of_staff
            }
          />
        </div>
        <div className="col-12 col-md-6">
          <CustomInput
            type="text"
            onChange={handleChange}
            name="role_title"
            value={
              isOnDuplicatePost ? duplicateData.role_title : newJob.role_title
            }
            label="Role title"
            id="role"
          />
        </div>
        <div className="col-12 col-md-6">
          <CustomInput
            type="text"
            onChange={handleChange}
            name="reports_to"
            value={
              isOnDuplicatePost ? duplicateData.reports_to : newJob.reports_to
            }
            label="Reports To"
            id="report"
          />
        </div>
        <div className="col-12 col-md-6" style={{ marginTop: "-4px" }}>
          <CustomSelect
            handleChange={handleChange}
            options={{ 0: "Select Level", ...levelOptions }}
            id="level"
            name="job_level"
            label="Job Levels"
            currentValue={
              isOnDuplicatePost ? duplicateData.job_level : newJob.job_level
            }
          />
        </div>

        <div className="col-12 col-md-6">
          <CustomSelect
            handleChange={handleChange}
            options={{
              0: "Select Type",
              "part-time": "Part-time",
              "full-time": "Full-time",
              contract: "Contract",
              internship: "Internship",
              volunteer: "Volunteer",
            }}
            id="job_type"
            name="job_type"
            label="Type of Employment"
            currentValue={
              isOnDuplicatePost ? duplicateData.job_type : newJob.job_type
            }
          />
        </div>

        <div className="col-12 col-md-6">
          <CustomSelect
            handleChange={handleChange}
            options={{ 0: "Select an Industry", ...industryOptions }}
            id="industry"
            name="industry"
            label="Industry"
            currentValue={
              isOnDuplicatePost ? duplicateData.industry : newJob.industry
            }
          />
        </div>
        <div className="col-12 col-md-6">
          <CustomSelect
            handleChange={handleChange}
            options={{
              0: "Select an Job Option",
              1: "Hybrid",
              2: "Remote",
              3: "In Person",
            }}
            id="worktype"
            name="worktype"
            label="Work Type"
            currentValue={newJob.worktype}
          />
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "18px" }}>
          <InputLabel name="country-label mt-3">Select Country</InputLabel>
          <Select
            labelId="country"
            name="country"
            label="country"
            value={state.country}
            onChange={handleChangeCountry}
            className="fullwidth-select"
          >
            <MenuItem value="">Select a Country</MenuItem>
            {countries.map(({ name, id }) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "-4px" }}>
          <CustomSelect
            handleChange={handleChange}
            options={(() => {
              const baseOptions = {
                0: "Select Job Tier",
                1: "Tier 1",
                2: "Tier 2",
              };

              // Only include Tier 3 if user has canPostTier3 permission
              if (userData && userData.canPostTier3 === "1") {
                baseOptions[3] = "Tier 3";
              }

              return baseOptions;
            })()}
            id="job_tier"
            name="job_tier"
            label="Job Tier"
            currentValue={currentTier}
          />
        </div>

        {currentTier === "2" && (
          <div className="col-12 col-md-6">
            <CustomInput
              type="number"
              onChange={handleChange}
              name="estimated_views"
              value={
                isOnDuplicatePost
                  ? duplicateData.estimated_views || ""
                  : newJob.estimated_views || ""
              }
              placeholder="Enter estimated views"
              label="Estimated Views"
              id="estimated_views"
              min="1"
            />
            <EstimatedViewsCalculation
              views={
                isOnDuplicatePost
                  ? duplicateData.estimated_views
                  : newJob.estimated_views
              }
              selectedCountry={state.country}
            />
          </div>
        )}

        <div className="col-12 col-md-12">
          <br />
          <label>About Company/Role</label>
          <div style={{ position: "relative" }}>
            <CKEditor
              editor={ClassicEditor}
              data={
                isOnDuplicatePost
                  ? duplicateData.about_company
                  : newJob.about_company
              }
              config={{}}
              onReady={(editor) => {
                editor.editing.view.change((writer) => {
                  writer.setStyle(
                    "min-height",
                    "320px",
                    editor.editing.view.document.getRoot()
                  );
                });
              }}
              onChange={(event, editor) => {
                const data = editor.getData();
                setNewJob({ ...newJob, about_company: data });
              }}
            />
            <AIJobDescriptionIcons
              onWriteWithAI={handleWriteWithAI}
              onRewriteWithAI={handleRewriteWithAI}
              isLoading={aiLoading}
              hasExistingContent={
                !!stripHtmlTags(
                  isOnDuplicatePost
                    ? duplicateData.about_company
                    : newJob.about_company
                )
              }
            />
          </div>
        </div>

        <div className="col-12 col-md-12">
          <SalaryRow
            handleChange={handleChange}
            salary_range={
              isOnDuplicatePost
                ? duplicateData.salary_range
                : newJob.salary_range
            }
            duration={
              isOnDuplicatePost ? duplicateData.duration : newJob.duration
            }
            currency={
              isOnDuplicatePost ? duplicateData.currency : newJob.currency
            }
          />
        </div>

        <div className="col-12 col-md-6" style={{ marginTop: "18px" }}>
          <FormControl variant="outlined" fullWidth className="w-100">
            <Autocomplete
              multiple
              freeSolo
              id="skills"
              options={(skillOptions || []).map(({ title }) => title)}
              value={selectedSkills}
              onChange={handleSkillsChange}
              filterSelectedOptions
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              filterOptions={(options, params) => {
                const filter = createFilterOptions();
                return filter(options, params);
              }}
              getOptionLabel={(option) => (typeof option === "string" ? option : option?.title || "")}
              renderOption={(option) => (typeof option === "string" ? option : option?.title || "")}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Skills"
                  placeholder="Add Skill (ex: Interior Design)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                />
              )}
            />
          </FormControl>
          <div className="skills-count mt-3 d-flex align-items-center">
            <span className="blob rounded px-2 py-1 bg-green co-white">
              <b>{selectedSkills.length}</b>
            </span>
            <span className="ml-2 co-green medium-text">Skills count</span>
          </div>
          {selectedSkills.length > 0 && (
            <div className="mt-5 w-100 d-flex flex-wrap">{displaySkills()}</div>
          )}
        </div>

        <div className="col-12" style={{ marginTop: "5.5%" }}>
          <RadioGroup
            name="isCompanyRepresentative"
            value={
              isOnDuplicatePost
                ? duplicateData.isCompanyRepresentative
                : newJob.isCompanyRepresentative
            }
            onChange={handleChange}
          >
            <label>Are you a company representative?</label>
            <div className="d-flex">
              <FormControlLabel
                value="1"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="0"
                control={<Radio color="primary" />}
                label="No"
              />
            </div>
          </RadioGroup>
        </div>
        {/* <div className="col-12">
          <RadioGroup
            name="canRecruiterRequest"
            value={
              isOnDuplicatePost
                ? duplicateData.canRecruiterRequest
                : newJob.canRecruiterRequest
            }
            onChange={handleChange}
          >
            <label>Allow recruiter reach out to you</label>
            <div className="d-flex">
              <FormControlLabel
                value="1"
                control={<Radio color="primary" />}
                label="Yes"
              />
              <FormControlLabel
                value="0"
                control={<Radio color="primary" />}
                label="No"
              />
            </div>
          </RadioGroup>
        </div> */}

        {/* <div className="col-12">
          <label>
            <strong>Post Job to Third-Party?</strong>
          </label>
          <br />
          <input
            type="checkbox"
            checked={externalPlatforms.includes("linkedin")}
            onChange={() => handleExternalPlatformChange("linkedin")}
          />{" "}
          Linkedin <br />
        </div> */}

        <div className="col-12 mt-3">
          <FormControlLabel
            control={
              <Checkbox
                checked={newJob.is_draft === "1"}
                onChange={(e) => {
                  const value = e.target.checked ? "1" : "0";
                  setNewJob({ ...newJob, is_draft: value });
                }}
                color="primary"
              />
            }
            label="Save as Draft"
          />
          <p className="small-text" style={{ color: '#6c757d', marginTop: '4px' }}>
            Draft jobs are not visible to the public and can be edited later.
          </p>
        </div>
      </div>
    </>
  );
};

JobDetails.propTypes = {
  newJob: object.isRequired,
  handleChange: func.isRequired,
  updateSelectedCountryID: func.isRequired,
  setNewJob: func.isRequired,
  industryOptions: object.isRequired,
  levelOptions: object.isRequired,
  skillOptions: object.isRequired,
  duplicateData: object,
  isOnDuplicatePost: bool,
};

JobDetails.defaultProps = {
  duplicateData: {},
  isOnDuplicatePost: false,
};

const EstimatedViewsCalculation = ({ views, selectedCountry }) => {
  const viewCount = parseInt(views) || 0;
  if (viewCount === 0) return null;

  const isNigeria = selectedCountry === "160";
  const ratePerView = isNigeria ? 189.98 : 0.12;
  const currency = isNigeria ? "NGN" : "USD";
  const currencySymbol = isNigeria ? "₦" : "$";
  const totalCost = viewCount * ratePerView;
  const formattedCost = totalCost.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <div className="mt-2 p-2 bg-light rounded">
      <small className="text-muted">
        <strong>Cost Calculation:</strong>
        <br />
        {viewCount.toLocaleString()} views × {currencySymbol}
        {ratePerView} per view = {currencySymbol}
        {formattedCost} {currency}
      </small>
    </div>
  );
};

const SalaryRow = ({
  handleChange,
  duration,
  salary_range,
  currency,
}) => {
  // salary_range is optional for all tiers
  const inputName = "salary_range";
  const inputLabel = "Salary Range (Optional)";

  const [displayValue, setDisplayValue] = useState(salary_range || "");

  useEffect(() => {
    setDisplayValue(salary_range !== undefined && salary_range !== null ? String(salary_range) : "");
  }, [salary_range]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setDisplayValue(value);
    handleChange({ target: { value, name: inputName } });
  };

  const handleBlur = (e) => {
    const text = e.target.value || "";
    if (/^\d+$/.test(text.replace(/,/g, ""))) {
      const stripped = text.replace(/,/g, "");
      const formatted = stripped
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      setDisplayValue(formatted);
      handleChange({ target: { value: formatted, name: inputName } });
    }
  };

  const handleCurrencyChange = (e) => {
    handleChange({ target: { value: e.target.value, name: "currency" } });
  };

  return (
    <div
      className="col-12"
      style={{
        margin: 0,
        padding: 0,
      }}
    >
      <div
        className="col"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          margin: 0,
          padding: 0,
        }}
      >
        <div className="col-12 col-md-4 p-0">
          <CustomInput
            type="text"
            name={inputName}
            label={inputLabel}
            id="report-salary"
            value={displayValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="e.g. 50000 or 40000 - 60000"
          />
        </div>
        <div className="col-12 col-md-4">
          <CustomSelect
            handleChange={handleCurrencyChange}
            options={{
              0: "Select Currency",
              ...CURRENCY_OPTIONS.reduce(
                (acc, option) => ({ ...acc, [option.value]: option.label }),
                {}
              ),
            }}
            id="currency"
            name="currency"
            label="Currency"
            currentValue={currency || "0"}
          />
        </div>
        <div className="col-12 col-md-4">
          <CustomSelect
            handleChange={handleChange}
            options={{
              0: "Select Duration",
              1: "Hourly",
              2: "Daily",
              3: "Weekly",
              4: "Monthly",
              5: "Annually",
            }}
            id="duration"
            name="duration"
            label="Duration"
            currentValue={duration}
          />
        </div>
      </div>
    </div>
  );
};

SalaryRow.propTypes = {
  handleChange: func.isRequired,
  duration: string,
  salary_range: string,
  currency: string,
};

export default JobDetails;
